import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Gauge } from "lucide-react";

function SetpointControl({ tag, onWrite, disabled }) {
  const [val, setVal] = useState(tag.value);
  const dirty = Number(val) !== Number(tag.value);
  return (
    <div className="bg-popover border border-border/60 rounded-sm p-3" data-testid={`control-${tag.tag_id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5 text-primary" />{tag.name}</span>
        <span className="font-mono text-xs text-muted-foreground">{tag.tag_id}</span>
      </div>
      <div className="flex items-center gap-3">
        <Slider
          data-testid={`slider-${tag.tag_id}`}
          value={[Number(val)]}
          min={tag.min}
          max={tag.max}
          step={10}
          disabled={disabled}
          onValueChange={(v) => setVal(v[0])}
          className="flex-1"
        />
        <span className="font-mono text-sm w-20 text-right">{Number(val).toFixed(0)} {tag.unit}</span>
      </div>
      <Button
        data-testid={`apply-${tag.tag_id}`}
        size="sm"
        disabled={disabled || !dirty}
        onClick={() => onWrite(tag.tag_id, Number(val))}
        className="rounded-sm font-semibold mt-2 w-full h-7 text-xs"
      >
        {dirty ? `Apply Setpoint → ${Number(val).toFixed(0)} ${tag.unit}` : "Setpoint Applied"}
      </Button>
    </div>
  );
}

function stateSemantic(tag, s) {
  if (["TRIGGERED", "BREACHED"].includes(s)) return "critical";
  if (tag.tag_id.includes("GUARD") && s === "OPEN") return "warning";
  if (s === "RUNNING" || s === "OPEN") return "normal";
  return "muted";
}

function StateControl({ tag, onWrite, disabled }) {
  const states = tag.states || [tag.value];
  const curSem = stateSemantic(tag, tag.value);
  const badge = {
    critical: "bg-status-critical/20 text-status-critical",
    warning: "bg-status-warning/20 text-status-warning",
    normal: "bg-status-normal/20 text-status-normal",
    muted: "bg-secondary text-muted-foreground",
  }[curSem];
  const activeBtn = {
    critical: "bg-status-critical text-white",
    warning: "bg-status-warning text-black",
    normal: "bg-status-normal text-white",
    muted: "bg-muted text-foreground",
  };
  return (
    <div className="bg-popover border border-border/60 rounded-sm p-3" data-testid={`control-${tag.tag_id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium">{tag.name}</span>
        <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded-sm ${badge}`}>{tag.value}</span>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {states.map((s) => {
          const active = tag.value === s;
          const sem = stateSemantic(tag, s);
          return (
            <Button
              key={s}
              data-testid={`set-${tag.tag_id}-${s}`}
              size="sm"
              disabled={disabled || active}
              onClick={() => onWrite(tag.tag_id, s)}
              className={`rounded-sm h-7 text-[11px] font-semibold ${active ? activeBtn[sem] : "bg-secondary hover:bg-accent text-foreground"}`}
            >
              {s}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

export default function ControlPanel({ tags, onWrite, disabled }) {
  const writable = tags.filter((t) => t.writable);
  const setpoints = writable.filter((t) => t.type === "setpoint");
  const states = writable.filter((t) => t.type !== "setpoint");

  return (
    <div className="bg-card border border-border/60 rounded-md" data-testid="control-panel">
      <div className="px-3 py-2 border-b border-border/60 bg-popover">
        <span className="label-ot">Operator Controls</span>
      </div>
      <div className="p-3 space-y-2.5">
        {states.map((t) => <StateControl key={t.tag_id} tag={t} onWrite={onWrite} disabled={disabled} />)}
        {setpoints.map((t) => <SetpointControl key={t.tag_id} tag={t} onWrite={onWrite} disabled={disabled} />)}
      </div>
    </div>
  );
}
